import React, { useEffect, useRef, useState } from 'react';

// Раніше компонент складав картинки у localStorage як base64-dataURL.
// Це шкодило: PNG-dataURL з canvas часто більший за вихідний webp, base64
// додає ще третину, і 5-мегабайтна квота швидко забивалась — через що
// не зберігався аватар (див. cachePhoto у PasswordScreen), а сам кеш
// періодично стирався. Після стирання іконки бралися з мережі й браузер
// встигав показати «зламану картинку» з alt-текстом.
// Кешуванням займається HTTP-кеш браузера — він для цього й створений.

// Разове прибирання спадку: звільняє місце, яке займав старий кеш
try {
  Object.keys(localStorage)
    .filter((k) => k.startsWith('img_cache_'))
    .forEach((k) => localStorage.removeItem(k));
} catch (e) { /* сховище недоступне — не критично */ }

// Адреси, які вже хоч раз завантажились у цій сесії. Такі картинки браузер
// малює з кешу одразу, тож при повторному монтуванні (повернення на вкладку
// «Картки», закриття деталей транзакції) не ховаємо їх — інакше вони
// на мить зникають і «блимають».
const loadedSrcs = new Set();

// Прелоад до входу (App): завантажує й декодує картинку і позначає її як
// побачену — тоді на головній вона малюється на першому кадрі, без
// прозорого проміжку та «доїзду» після введення ПІН-коду.
export function preloadImage(src) {
  if (!src || loadedSrcs.has(src)) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    const done = () => {
      if (img.naturalWidth > 0) loadedSrcs.add(src);
      resolve();
    };
    img.onload = () => {
      if (img.decode) img.decode().then(done, done);
      else done();
    };
    img.onerror = () => resolve();
    img.src = src;
  });
}

export default function ImageWithCache({ src, alt = '', className = '', style, ...props }) {
  // Поки картинка не завантажилась, тримаємо її прозорою: інакше браузер
  // показує іконку «зламане зображення» з alt-текстом.
  const [loaded, setLoaded] = useState(() => loadedSrcs.has(src));
  const imgRef = useRef(null);
  const prevSrcRef = useRef(src);

  // Картинка з кешу встигає завантажитись до того, як React повісить onLoad,
  // і подія губиться — тоді вона лишилась би прозорою назавжди. Тому після
  // монтування звіряємося з img.complete. При зміні src — ховаємо, доки нова
  // не завантажиться (якщо її ще не бачили).
  useEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      setLoaded(loadedSrcs.has(src));
    }
    const el = imgRef.current;
    if (el && el.complete) {
      if (el.naturalWidth > 0) loadedSrcs.add(src);
      setLoaded(true);
    }
  }, [src]);

  const show = () => {
    if (imgRef.current?.naturalWidth > 0) loadedSrcs.add(src);
    setLoaded(true);
  };

  const seen = loadedSrcs.has(src);

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={className}
      // уже завантажену картинку декодуємо синхронно — щоб вона була
      // на першому ж кадрі після монтування, без порожнього проміжку
      decoding={seen ? 'sync' : 'async'}
      onLoad={show}
      onError={show}
      style={{ ...style, opacity: loaded ? undefined : 0 }}
      {...props}
    />
  );
}
